import {
  Autocomplete,
  type AutocompleteProps,
  createFilterOptions,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import { useGetCollectionsQuery } from '../api'
import type { Collection } from '../types'

export type CollectionsValue =
  | string
  | ({
      collectionName: string
      inputValue?: string
    } & Partial<Collection>)

type AcProps = AutocompleteProps<CollectionsValue, false, false, true>
type Props = Omit<AcProps, 'options' | 'renderInput'>

export const CollectionSelect = (props: Props) => {
  const [value, setValue] = useState<CollectionsValue | null>(null)
  const { data: options = [] as CollectionsValue[], ...request } =
    useGetCollectionsQuery(undefined, { refetchOnMountOrArgChange: true })
  const { onChange, ...autoCompleteProps } = props

  const handleChange: AcProps['onChange'] = (
    event,
    newValue,
    reason,
    details,
  ) => {
    let nextValue
    if (typeof newValue === 'string') {
      nextValue = { collectionName: newValue }
    } else if (newValue && newValue.inputValue) {
      nextValue = { collectionName: newValue.inputValue }
    } else {
      nextValue = newValue
    }

    setValue(nextValue)

    if (onChange) onChange(event, nextValue, reason, details)
  }

  return (
    <Autocomplete
      value={value}
      loading={request.isLoading}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => <TextField {...params} />}
      options={options}
      clearOnBlur
      selectOnFocus
      handleHomeEndKeys
      filterOptions={(options, params) => {
        const filtered = filter(options, params)
        const { inputValue } = params
        const isExisting = options.some(
          (option) =>
            typeof option !== 'string' && inputValue === option?.collectionName,
        )

        if (inputValue !== '' && !isExisting) {
          filtered.push({
            inputValue,
            collectionName: `Add "${inputValue}"`,
          })
        }

        return filtered
      }}
      freeSolo
      onChange={handleChange}
      {...autoCompleteProps}
    />
  )
}

const filter = createFilterOptions<CollectionsValue>()

const getOptionLabel = (option: CollectionsValue | string) => {
  if (typeof option === 'string') {
    return option
  }

  if (option.inputValue) return option.inputValue

  return option.collectionName
}

const getOptionKey = (item: CollectionsValue) => {
  if (typeof item === 'string') {
    return item
  } else {
    return item?.collectionCode ?? 0
  }
}
